import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import apiFetch from "../../../shared/services/apiClient";
import { getAvisos, crearAviso, deleteAviso } from "../services/portalService";
import { overlayClose } from "../../../shared/utils/overlayClose";
import { TableToolbar, FilterField, FilterSelect, FilterInput } from "../../finance/shared/TableToolbar";
import styles from "../../finance/invoices/styles/invoice.module.css";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useBreakpoint() {
  const get = () => window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const fn = () => setBp(get());
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return bp;
}

const PAGE_SIZE = 10;

function StatCard({ icon, label, value, color = "#f97316" }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 12, padding: "16px 20px", display: "flex",
      alignItems: "center", gap: 14, flex: "1 1 140px", minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color, fontSize: 16,
      }}>
        <i className={`fa-solid ${icon}`} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 3, fontWeight: 500, whiteSpace: "nowrap" }}>{label}</div>
      </div>
    </div>
  );
}

function Pagination({ page, total, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const btn = (disabled, onClick, children) => (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-card)",
      background: "var(--bg-card)", color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
      cursor: disabled ? "default" : "pointer", fontSize: 13, opacity: disabled ? 0.4 : 1,
    }}>
      {children}
    </button>
  );
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "12px 16px", borderTop: "1px solid var(--border-card)",
    }}>
      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
        {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {btn(page === 1, () => onChange(page - 1), <i className="fa-solid fa-chevron-left" />)}
        <span style={{ padding: "5px 12px", borderRadius: 8, fontSize: 13, background: "#f97316", color: "#fff", fontWeight: 700 }}>
          {page} / {pages}
        </span>
        {btn(page === pages, () => onChange(page + 1), <i className="fa-solid fa-chevron-right" />)}
      </div>
    </div>
  );
}

export default function AdminAvisos() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();
  const bp = useBreakpoint();
  const isMobile = bp === "mobile";
  const fileRef = useRef();

  const [avisos,   setAvisos]   = useState([]);
  const [filiales, setFiliales] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [form,     setForm]     = useState({ titulo: "", mensaje: "", destinatarios: "TODOS", filiales_ids: [] });
  const [file,     setFile]     = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [confirm,  setConfirm]  = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [search,       setSearch]       = useState("");
  const [filterFilial, setFilterFilial] = useState("");
  const [filterDesde,  setFilterDesde]  = useState("");
  const [filterHasta,  setFilterHasta]  = useState("");
  const [page,         setPage]         = useState(1);

  function load() {
    getAvisos().then(d => setAvisos(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/").then(r => r.json()).then(d => setFiliales(d.results || d)).catch(() => {});
  }, []);

  const filtered = avisos.filter(a => {
    if (search && !a.titulo.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterFilial) {
      const fid = parseInt(filterFilial, 10);
      if (a.destinatarios !== "TODOS" && !(a.filiales_ids || []).includes(fid)) return false;
    }
    const fecha = a.created_at?.split("T")[0] || "";
    if (filterDesde && fecha < filterDesde) return false;
    if (filterHasta && fecha > filterHasta) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Stats
  const hace7 = new Date(Date.now() - 7 * 864e5).toISOString().split("T")[0];
  const recientes = avisos.filter(a => (a.created_at?.split("T")[0] || "") >= hace7).length;
  const aTodos = avisos.filter(a => a.destinatarios === "TODOS").length;

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("titulo", form.titulo);
      fd.append("mensaje", form.mensaje);
      fd.append("destinatarios", form.destinatarios);
      form.filiales_ids.forEach(id => fd.append("filiales_ids", id));
      if (file) fd.append("archivo", file);
      await crearAviso(fd);
      showToast(t("portal.avisoEnviado"), true);
      setForm({ titulo: "", mensaje: "", destinatarios: "TODOS", filiales_ids: [] });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
      setTimeout(() => setModal(false), 1200);
    } catch (e) {
      try { const d = JSON.parse(e.message); showToast(Object.values(d).flat().join(". "), false); }
      catch { showToast(e.message, false); }
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!confirm) return;
    setDeleting(true);
    try { await deleteAviso(confirm.id); showToast(t("common.eliminado"), true); setConfirm(null); load(); }
    catch { showToast(t("common.errorEliminar"), false); }
    finally { setDeleting(false); }
  }

  function toggleFilial(id) {
    setForm(f => ({
      ...f,
      filiales_ids: f.filiales_ids.includes(id)
        ? f.filiales_ids.filter(x => x !== id)
        : [...f.filiales_ids, id],
    }));
  }

  const fldStyle = {
    width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8,
    border: "1px solid var(--border-card)", background: "var(--bg-input, var(--hover-bg))",
    color: "var(--text-primary)", fontSize: 13, outline: "none",
  };
  const labelSt = {
    fontSize: 11, fontWeight: 700, color: "var(--text-secondary)",
    display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5,
  };
  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      {toastEl}
      <ConfirmModal open={!!confirm} name={confirm?.name} onConfirm={handleDelete} onCancel={() => setConfirm(null)} loading={deleting} />

      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Portal Admin /</span>
        </Link>
        <span className={styles.active}> {t("portal.avisosAdmin")}</span>
      </span>

      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12, margin: "8px 0 20px",
      }}>
        <div>
          <h2 className="title" style={{ margin: 0, color: "var(--text-primary)" }}>
            {t("portal.avisosAdmin")}
          </h2>
          <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: "1rem" }}>
            {t("portal.avisosAdminDesc")}
          </p>
        </div>
        <button onClick={() => {
          setForm({ titulo: "", mensaje: "", destinatarios: "TODOS", filiales_ids: [] });
          setFile(null);
          setModal(true);
        }} style={{
          background: "#f97316", color: "#fff", border: "none", borderRadius: 6,
          padding: "8px 14px", cursor: "pointer", fontWeight: 600, fontSize: "0.82rem",
          display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
        }}
          onMouseOver={(e) => (e.currentTarget.style.background = "#ea6b0c")}
          onMouseOut={(e)  => (e.currentTarget.style.background = "#f97316")}
        >
          <i className="fa-solid fa-paper-plane" />
          {isMobile ? "Enviar" : t("portal.enviar")}
        </button>
      </div>

      {/* Stats */}
      {!loading && avisos.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
          <StatCard icon="fa-bell"         label="Total enviados"    value={avisos.length}    color="#f97316" />
          <StatCard icon="fa-clock-rotate-left" label="Últimos 7 días" value={recientes}       color="#3b82f6" />
          <StatCard icon="fa-users"        label="A todos"          value={aTodos}            color="#10b981" />
          <StatCard icon="fa-filter"       label="Por filial"       value={avisos.length - aTodos} color="#8b5cf6" />
        </div>
      )}

      {/* Tabla */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 8, overflow: "hidden", overflowX: "auto",
      }}>
        {/* Barra de filtros unificada — igual que Catálogo / Facturas */}
        <TableToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Buscar por título…"
          loading={loading}
          activeFilterCount={[filterFilial, filterDesde, filterHasta].filter(Boolean).length}
          onClearFilters={() => { setFilterFilial(""); setFilterDesde(""); setFilterHasta(""); setPage(1); }}
          rightActions={
            <span style={{
              fontSize: "0.72rem", color: "var(--text-secondary)",
              background: "var(--hover-bg)", borderRadius: 20, padding: "4px 10px",
              fontWeight: 600, whiteSpace: "nowrap",
            }}>
              {filtered.length} aviso{filtered.length !== 1 ? "s" : ""}
            </span>
          }
          filters={
            <>
              <FilterField label="Filial">
                <FilterSelect value={filterFilial} onChange={(e) => { setFilterFilial(e.target.value); setPage(1); }}>
                  <option value="">Todas las filiales</option>
                  {filiales.map((f) => <option key={f.id} value={f.id}>Filial {f.idFilial}</option>)}
                </FilterSelect>
              </FilterField>
              <FilterField label="Desde">
                <FilterInput type="date" value={filterDesde} onChange={(e) => { setFilterDesde(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label="Hasta">
                <FilterInput type="date" value={filterHasta} onChange={(e) => { setFilterHasta(e.target.value); setPage(1); }} />
              </FilterField>
            </>
          }
        />

        {/* Cabecera columnas */}
        {!loading && filtered.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 130px 110px 90px 64px",
            gap: 10, padding: "6px 14px", minWidth: 620,
            borderBottom: "1px solid var(--border-card)", background: "var(--hover-bg)",
          }}>
            {["Título", "Destinatario", "Fecha", "Adjunto", "Acciones"].map((h, i) => (
              <span key={i} style={{
                fontSize: "0.65rem", color: "var(--text-muted)", fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.5px",
                textAlign: i >= 2 ? "center" : "left",
              }}>
                {h}
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#f97316", fontSize: "1.2rem", marginBottom: 8, display: "block" }} />
            Cargando avisos…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <div style={{
              width: 60, height: 60, borderRadius: "50%",
              background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px", color: "#f97316", fontSize: 24,
            }}>
              <i className="fa-solid fa-bell-slash" />
            </div>
            <p style={{ margin: 0, fontWeight: 700, color: "var(--text-primary)", fontSize: "0.95rem" }}>
              {avisos.length === 0 ? t("portal.sinAvisosEnviados") : "Sin resultados para este filtro."}
            </p>
            <p style={{ margin: "6px 0 0", color: "var(--text-muted)", fontSize: "0.85rem" }}>
              {avisos.length === 0 ? "Enviá el primer aviso a los residentes." : "Probá cambiando los filtros."}
            </p>
            {avisos.length === 0 && (
              <button onClick={() => setModal(true)} style={{
                marginTop: 16, background: "#f97316", color: "#fff", border: "none",
                borderRadius: 8, padding: "9px 18px", cursor: "pointer", fontWeight: 700, fontSize: 13,
                display: "flex", alignItems: "center", gap: 6, margin: "16px auto 0",
              }}>
                <i className="fa-solid fa-paper-plane" /> Enviar primer aviso
              </button>
            )}
          </div>
        ) : (
          <>
            {paged.map((a) => (
              <div key={a.id} style={{
                display: "grid", gridTemplateColumns: "1fr 130px 110px 90px 64px",
                gap: 10, alignItems: "center", padding: "10px 14px", minWidth: 620,
                borderBottom: "1px solid var(--border-card)", transition: "background 0.12s",
              }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--hover-bg)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.titulo}
                  </p>
                  <p style={{
                    margin: "2px 0 0", fontSize: "0.78rem", color: "var(--text-muted)",
                    display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden",
                  }}>
                    {a.mensaje}
                  </p>
                </div>
                <div>
                  <span style={{
                    background: a.destinatarios === "TODOS" ? "rgba(16,185,129,0.1)" : "rgba(139,92,246,0.1)",
                    border: `1px solid ${a.destinatarios === "TODOS" ? "rgba(16,185,129,0.25)" : "rgba(139,92,246,0.25)"}`,
                    color: a.destinatarios === "TODOS" ? "#10b981" : "#8b5cf6",
                    borderRadius: 20, padding: "3px 9px", fontSize: "0.63rem", fontWeight: 700,
                    whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4,
                  }}>
                    <i className={`fa-solid ${a.destinatarios === "TODOS" ? "fa-users" : "fa-filter"}`} style={{ fontSize: 9 }} />
                    {a.destinatarios === "TODOS" ? "Todos" : "Por filial"}
                  </span>
                </div>
                <span style={{ fontSize: "0.78rem", color: "var(--text-secondary)", textAlign: "center" }}>
                  {a.created_at?.split("T")[0]}
                </span>
                <div style={{ textAlign: "center" }}>
                  {a.archivo_url && (
                    <a href={a.archivo_url} target="_blank" rel="noreferrer"
                      style={{ color: "#3b82f6", fontSize: "0.85rem" }} title="Ver adjunto">
                      <i className="fa-solid fa-paperclip" />
                    </a>
                  )}
                </div>
                <div style={{ textAlign: "center" }}>
                  <button onClick={() => setConfirm({ id: a.id, name: a.titulo })} style={{
                    background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)",
                    borderRadius: 6, padding: "5px 9px", cursor: "pointer", color: "#f87171", fontSize: "0.78rem",
                  }}>
                    <i className="fa-solid fa-trash" />
                  </button>
                </div>
              </div>
            ))}
            <Pagination page={page} total={filtered.length} onChange={setPage} />
          </>
        )}
      </div>

      {/* ── Create modal ── */}
      {modal && (
        <div {...overlayClose(() => setModal(false))} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          backdropFilter: "blur(4px)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 16, width: "min(580px, 96vw)",
            boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
            maxHeight: "92vh", overflowY: "auto",
          }}>
            {/* Header */}
            <div style={{
              padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#f97316", fontSize: 16,
                }}>
                  <i className="fa-solid fa-paper-plane" />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>
                    {t("portal.nuevoAviso")}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
                    Enviá un mensaje a los residentes del condominio
                  </div>
                </div>
              </div>
              <button onClick={() => setModal(false)} style={{
                background: "none", border: "none", color: "var(--text-muted)",
                cursor: "pointer", fontSize: 18, padding: 4,
              }}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Título */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#f97316", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="fa-solid fa-pen" style={{ fontSize: 10 }} /> Contenido
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div>
                      <label style={labelSt}>{t("portal.titulo")} <span style={{ color: "#f87171" }}>*</span></label>
                      <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} required
                        style={fldStyle} placeholder="Ej: Mantenimiento programado, Reunión de condóminos…" />
                    </div>
                    <div>
                      <label style={labelSt}>{t("portal.mensaje")} <span style={{ color: "#f87171" }}>*</span></label>
                      <textarea value={form.mensaje} onChange={e => setForm(f => ({ ...f, mensaje: e.target.value }))} required rows={5}
                        style={{ ...fldStyle, resize: "vertical" }} placeholder="Escribí el contenido del aviso aquí…" />
                    </div>
                  </div>
                </div>

                {/* Destinatarios */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#f97316", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="fa-solid fa-users" style={{ fontSize: 10 }} /> Destinatarios
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    {["TODOS", "SELECCIONADOS"].map(op => (
                      <button key={op} type="button"
                        onClick={() => setForm(f => ({ ...f, destinatarios: op, filiales_ids: [] }))}
                        style={{
                          flex: 1, padding: "9px 12px", borderRadius: 8, cursor: "pointer",
                          fontWeight: 600, fontSize: 13,
                          border: `1px solid ${form.destinatarios === op ? "#f97316" : "var(--border-card)"}`,
                          background: form.destinatarios === op ? "rgba(249,115,22,0.1)" : "var(--hover-bg)",
                          color: form.destinatarios === op ? "#f97316" : "var(--text-secondary)",
                          transition: "all 0.15s",
                        }}>
                        <i className={`fa-solid ${op === "TODOS" ? "fa-users" : "fa-filter"}`} style={{ marginRight: 6 }} />
                        {op === "TODOS" ? t("portal.todasLasFiliales") : t("portal.filialesSeleccionadas")}
                      </button>
                    ))}
                  </div>
                  {form.destinatarios === "SELECCIONADOS" && (
                    <div>
                      <label style={{ ...labelSt, marginBottom: 8 }}>{t("portal.seleccionarFiliales")}</label>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {filiales.map(f => (
                          <button key={f.id} type="button" onClick={() => toggleFilial(f.id)} style={{
                            padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600,
                            border: `1px solid ${form.filiales_ids.includes(f.id) ? "#f97316" : "var(--border-card)"}`,
                            background: form.filiales_ids.includes(f.id) ? "#f97316" : "var(--hover-bg)",
                            color: form.filiales_ids.includes(f.id) ? "#fff" : "var(--text-secondary)",
                            transition: "all 0.12s",
                          }}>
                            <i className="fa-solid fa-building" style={{ marginRight: 4, fontSize: 10 }} />
                            {f.idFilial}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Adjunto */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#f97316", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="fa-solid fa-paperclip" style={{ fontSize: 10 }} /> Archivo adjunto (opcional)
                  </div>
                  <div onClick={() => fileRef.current?.click()} style={{
                    border: `1px dashed ${file ? "#f97316" : "var(--border-card)"}`,
                    borderRadius: 10, padding: 16, textAlign: "center", cursor: "pointer",
                    background: file ? "rgba(249,115,22,0.06)" : "var(--hover-bg)",
                    transition: "all 0.15s",
                  }}>
                    <i className={`fa-solid ${file ? "fa-file-check" : "fa-cloud-arrow-up"}`}
                      style={{ color: file ? "#f97316" : "var(--text-secondary)", fontSize: 22, display: "block", marginBottom: 6 }} />
                    <span style={{ fontSize: 13, color: file ? "#f97316" : "var(--text-secondary)", fontWeight: 500 }}>
                      {file ? file.name : t("portal.adjuntarArchivo")}
                    </span>
                  </div>
                  <input ref={fileRef} type="file" style={{ display: "none" }} onChange={e => setFile(e.target.files[0] || null)} />
                  {file && (
                    <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} style={{
                      marginTop: 6, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)",
                      color: "#f87171", borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600,
                    }}>
                      <i className="fa-solid fa-trash" style={{ marginRight: 4 }} /> Quitar adjunto
                    </button>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div style={{
                padding: "16px 24px 20px", borderTop: "1px solid var(--border-card)",
                display: "flex", gap: 10, justifyContent: "flex-end",
              }}>
                <button type="button" onClick={() => setModal(false)} style={{
                  padding: "9px 20px", borderRadius: 8, border: "1px solid var(--border-card)",
                  background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
                }}>
                  {t("common.cancelar")}
                </button>
                <button type="submit" disabled={saving} style={{
                  padding: "9px 22px", borderRadius: 8, border: "none",
                  background: saving ? "#b45309" : "#f97316",
                  color: "#fff", fontWeight: 700, fontSize: 14,
                  cursor: saving ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  {saving
                    ? <><i className="fa-solid fa-circle-notch fa-spin" /> Enviando…</>
                    : <><i className="fa-solid fa-paper-plane" /> {t("portal.enviarAviso")}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
